import { Close } from '@mui/icons-material';
import { Drawer, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import { ControlledDateRangePicker } from '../../../common/ControlledDateRangePicker';
import { addNewBookingSchema, AddNewBookingSchema } from './addNewBooking.schema';
import { useTelegram } from '../../../../hooks/useTelegram';
import { useSnackbarContext } from '../../../common/SnackbarProvider/SnackbarProvider';
import { MapConfig, OfficeMap } from '../../../common/OfficeMap/OfficeMap';
import { officeSpaceBookingApi } from '../../../../api/officeSpaceBookingApi';
import { ControlledClientAutocomplete } from '../../../common/ControlledClientAutocomplete';
import { TeamAssignmentList } from '../../DeskManagementLayout/TeamAssignmentList/TeamAssignmentList';
import BookingDate from '../BookingDate/BookingDate';
import { isMultiDayBooking } from '../../../../constants';

type AddNewBookingsDrawerProps = {
    open: boolean;
    onClose: () => void;
    usersSelectOptions: { label: string; value: string }[];
};

const officeMapConfig: MapConfig = [
    { id: 'MR1', options: { disabled: true } },
    { id: 'MR2', options: { disabled: true } },
    { id: 'MR3', options: { disabled: true } },
];

export const AddNewBookingsDrawer: React.FC<AddNewBookingsDrawerProps> = ({
    onClose,
    open,
    usersSelectOptions,
}) => {
    const [bookOfficeSpace, { isLoading }] = officeSpaceBookingApi.useCreateMutation();

    const [activeStep, setActiveStep] = useState<number>(0);

    const { queryId } = useTelegram();

    const { showSnackbar } = useSnackbarContext();

    const {
        control,
        watch,
        setValue,
        getValues,
        handleSubmit,
        reset,
        formState: { dirtyFields },
    } = useForm<AddNewBookingSchema>({
        resolver: zodResolver(addNewBookingSchema),
        defaultValues: {
            userId: undefined,
            endDate: new Date().toISOString(),
            startDate: new Date().toISOString(),
        },
    });

    const userId = watch('userId');

    const handleCloseDrawer = () => {
        setActiveStep(0);
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<AddNewBookingSchema> = async (data) => {
        await bookOfficeSpace({
            queryId: queryId || '',
            spaceName: data.spaceName,
            startDate: data.startDate!,
            userId: data.userId,
            endDate: data.endDate,
        })
            .unwrap()
            .then(() => {
                showSnackbar('Office Space Booked Successfully', 'success');
                reset();
                handleCloseDrawer();
            })
            .catch((error: { status: number; code: string }) => {
                if (error.status === 403) {
                    showSnackbar("User's team can not book this office space", 'error');
                }
                if (error.status === 404) {
                    showSnackbar('User or office space does not exist', 'error');
                }
                if (error.status === 409) {
                    showSnackbar(
                        'This user has another active booking for this time period',
                        'error',
                    );
                }
                if (error.status === 500) {
                    showSnackbar('Internal Server Error', 'error');
                }
            });
    };

    const handleNextStep = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleOfficeSpaceClick = (value: string) => {
        setValue('spaceName', value, { shouldDirty: true });
    };

    const handleButtonClick = activeStep === 0 ? handleNextStep : handleSubmit(onSubmit);

    const isSubmitButtonEnabled = dirtyFields.userId && userId;

    const isPeriodBooking = isMultiDayBooking(watch('startDate'), watch('endDate'));

    return (
        <Drawer open={open} onClose={handleCloseDrawer}>
            <Stack gap={1} p={3} sx={{ width: '100vw', height: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Add New Booking</Typography>
                    <Close onClick={handleCloseDrawer} sx={{ cursor: 'pointer' }} />
                </Stack>

                <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
                    <Stack height="100%">
                        {activeStep === 0 && (
                            <Stack gap={2} flexGrow={1}>
                                <ControlledClientAutocomplete
                                    control={control}
                                    name="userId"
                                    options={usersSelectOptions}
                                    label="User"
                                />

                                <ControlledDateRangePicker
                                    control={control}
                                    watch={watch}
                                    setValue={setValue}
                                    getValues={getValues}
                                    config={[
                                        {
                                            name: 'startDate',
                                            label: 'Start Date',
                                            minDate: new Date().toISOString(),
                                        },
                                        {
                                            name: 'endDate',
                                            label: 'End Date',
                                            minDate: watch('startDate') ?? undefined,
                                        },
                                    ]}
                                />
                            </Stack>
                        )}
                        {activeStep === 1 && (
                            <Stack>
                                <BookingDate
                                    startDateState={getValues('startDate')}
                                    endDateState={getValues('endDate')}
                                    editWorkPlaceActive={false}
                                    setActiveStep={setActiveStep}
                                    setValue={setValue}
                                    periodActive={isPeriodBooking}
                                />
                                <TeamAssignmentList />
                                <OfficeMap
                                    applyDefaultConfig
                                    config={officeMapConfig}
                                    selectedSpace={watch('spaceName')}
                                    onOfficeSpaceClick={handleOfficeSpaceClick}
                                    startDate={watch('startDate')}
                                    endDate={watch('endDate')}
                                    userId={getValues('userId')}
                                    showBookerDataForBookedDesks={!isPeriodBooking}
                                    key={watch('startDate')}
                                />
                            </Stack>
                        )}
                        <Stack>
                            {((activeStep === 0 && isSubmitButtonEnabled) ||
                                (activeStep === 1 && watch('spaceName'))) && (
                                <LoadingButton
                                    onClick={handleButtonClick}
                                    disabled={activeStep === 0 && !isSubmitButtonEnabled}
                                    variant="contained"
                                    loading={isLoading}
                                    type={activeStep === 0 ? 'button' : 'submit'}
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: '#40a7e3',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        padding: '10px 20px',
                                        fontFamily:
                                            '"Helvetica Neue", Helvetica, Arial, sans-serif',
                                        ':hover': {
                                            backgroundColor: '#40a7e3',
                                        },
                                    }}
                                >
                                    {activeStep === 0 ? 'Apply' : `Book ${watch('spaceName')}`}
                                </LoadingButton>
                            )}
                        </Stack>
                    </Stack>
                </form>
            </Stack>
        </Drawer>
    );
};
